import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0, withAuthenticationRequired } from '@auth0/auth0-react';
import { useUserStatus } from '../context/UserStatusContext';
import { submitPersonalityTest } from '../services/apiService';
import {
  IPIP_NEO_120_QUESTIONS,
  IPIP_NEO_50_QUESTIONS,
} from '../content/ipipNeoContent';
import { IPIP_NEO_FACETS } from '../content/personaContent';
import styles from './PersonalityTestPage.module.css';

const options = [
  { value: 1, label: 'Very Inaccurate' },
  { value: 2, label: 'Moderately Inaccurate' },
  { value: 3, label: 'Neutral' },
  { value: 4, label: 'Moderately Accurate' },
  { value: 5, label: 'Very Accurate' },
];
const QUESTIONS_PER_PAGE = 10;

function PersonalityTestPage() {
  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const { userStatus, refreshUserStatus } = useUserStatus();

  const [selectedTest, setSelectedTest] = useState(null);
  const [answers, setAnswers] = useState({});
  const [currentPage, setCurrentPage] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentPage]);

  // --- START: Dev-only Auto-fill Logic ---
  const handleAutoFillAndProceed = () => {
    const updatedAnswers = { ...answers };
    currentQuestions.forEach((q, index) => {
      const questionIndex = currentPage * QUESTIONS_PER_PAGE + index;
      if (!updatedAnswers[questionIndex]) {
        updatedAnswers[questionIndex] = Math.floor(Math.random() * 5) + 1;
      }
    });
    setAnswers(updatedAnswers);

    setTimeout(() => {
      if (currentPage < totalPages - 1) {
        setCurrentPage(currentPage + 1);
      } else {
        handleSubmit(updatedAnswers);
      }
    }, 100);
  };
  // --- END: Dev-only Auto-fill Logic ---

  const isPro =
    userStatus?.planTier === 'pro' ||
    userStatus?.planTier === 'founder' ||
    userStatus?.planTier === 'vip';
  const questions =
    selectedTest === 'quick' ? IPIP_NEO_50_QUESTIONS : IPIP_NEO_120_QUESTIONS;

  const totalPages = Math.ceil(questions.length / QUESTIONS_PER_PAGE);
  const currentQuestions = questions.slice(
    currentPage * QUESTIONS_PER_PAGE,
    (currentPage + 1) * QUESTIONS_PER_PAGE
  );

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = questions.length;

  const handleAnswerChange = (questionIndex, value) => {
    setAnswers((prev) => ({ ...prev, [questionIndex]: value }));
  };

  const calculateScores = () => {
    const isQuick = selectedTest === 'quick';
    const finalScores = {};

    if (isQuick) {
      const domainScores = {
        openness: [],
        conscientiousness: [],
        extraversion: [],
        agreeableness: [],
        neuroticism: [],
      };
      questions.forEach((q, index) => {
        let value = answers[index] || 3;
        if (q.reversed) value = 6 - value;
        domainScores[q.domain].push(value);
      });
      Object.keys(domainScores).forEach((domain) => {
        const domainScoreSum = domainScores[domain].reduce((a, b) => a + b, 0);
        const normalizedDomain = Math.round(((domainScoreSum - 10) / 40) * 100);
        finalScores[`${domain}Score`] = Math.max(
          1,
          Math.min(100, normalizedDomain)
        );
      });
    } else {
      finalScores.facets = {};
      Object.keys(IPIP_NEO_FACETS).forEach((domain) => {
        finalScores.facets[domain] = {};
        Object.keys(IPIP_NEO_FACETS[domain].facets).forEach((facet) => {
          finalScores.facets[domain][facet] = 0;
        });
      });
      questions.forEach((q, index) => {
        let value = answers[index] || 3;
        if (q.reversed) value = 6 - value;
        finalScores.facets[q.domain][q.facet] += value;
      });
      Object.keys(finalScores.facets).forEach((domain) => {
        Object.keys(finalScores.facets[domain]).forEach((facet) => {
          const score = finalScores.facets[domain][facet];
          const normalized = Math.round(((score - 4) / 16) * 100);
          finalScores.facets[domain][facet] = Math.max(
            1,
            Math.min(100, normalized)
          );
        });
      });
    }
    return finalScores;
  };

  const handleSubmit = async (finalAnswers = answers) => {
    setIsSaving(true);
    try {
      const personalityScores = calculateScores(finalAnswers);
      const accessToken = await getAccessTokenSilently();
      const payload = {
        personalityData: {
          ...(selectedTest === 'quick'
            ? personalityScores
            : personalityScores.facets),
        },
      };
      await submitPersonalityTest(payload, accessToken);
      await refreshUserStatus();
      navigate('/passport');
    } catch (error) {
      console.error('Failed to submit test:', error);
    } finally {
      setIsSaving(false);
    }
  };

  if (!selectedTest) {
    return (
      <div className={styles.chooserContainer}>
        <h2>Personality Assessment</h2>
        <p>
          Choose your assessment method. A deeper analysis provides more nuanced
          results.
        </p>
        <div className={styles.testChooser}>
          <div
            className={styles.choiceCard}
            onClick={() => setSelectedTest('quick')}
          >
            <h4>🚀 Basic Assessment</h4>
            <p>
              A 50-question test to establish your 5 core personality traits.
            </p>
          </div>
          <div
            className={`${styles.choiceCard} ${!isPro && styles.locked}`}
            onClick={() => isPro && setSelectedTest('full')}
          >
            {!isPro && <div className={styles.proBadge}>Pro</div>}
            <h4>🔬 Deep Dive Assessment</h4>
            <p>
              The full 120-question test for a detailed, 30-facet analysis and
              the "Verified" badge.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.testContainer}>
      {/* --- DEV ONLY BUTTON --- */}
      {process.env.NODE_ENV === 'development' && (
        <button
          onClick={handleAutoFillAndProceed}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            backgroundColor: 'orange',
          }}
          className="action-button secondary"
        >
          Auto-Fill & Next
        </button>
      )}
      {/* --- END DEV ONLY BUTTON --- */}
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${((currentPage + 1) / totalPages) * 100}%` }}
        />
      </div>

      {currentQuestions.map((q, index) => {
        const questionIndex = currentPage * QUESTIONS_PER_PAGE + index;
        return (
          <div
            key={questionIndex}
            className={styles.questionItem}
            data-testid="question-item"
          >
            <p className={styles.questionText}>
              {questionIndex + 1}. {q.text}
            </p>
            <div className={styles.radioGrid}>
              {options.map((opt) => (
                <label key={opt.value} className={styles.radioLabel}>
                  <input
                    type="radio"
                    name={`q-${questionIndex}`}
                    value={opt.value}
                    checked={answers[questionIndex] === opt.value}
                    onChange={() =>
                      handleAnswerChange(questionIndex, opt.value)
                    }
                    className={styles.radioInput}
                  />
                  <span className={styles.radioControl}></span>
                  <span className={styles.radioText}>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>
        );
      })}

      <div className={styles.navigation}>
        <button
          onClick={() =>
            currentPage > 0
              ? setCurrentPage(currentPage - 1)
              : setSelectedTest(null)
          }
          className="action-button secondary"
        >
          {currentPage > 0 ? 'Previous' : 'Back'}
        </button>
        <span className={styles.progressText}>
          {answeredCount} / {totalQuestions} Answered
        </span>
        {currentPage < totalPages - 1 ? (
          <button
            onClick={() => setCurrentPage(currentPage + 1)}
            className="action-button primary"
          >
            Next
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            className="action-button primary"
            disabled={isSaving}
          >
            {isSaving ? 'Submitting...' : 'Finish & Calculate'}
          </button>
        )}
      </div>
    </div>
  );
}

export default withAuthenticationRequired(PersonalityTestPage);
